import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { getSupabase } from "@/integrations/supabase/lazy";
import { FRONTEND_PAGES } from "@/pages/cms/frontendPages";
import { useSeason } from "@/hooks/useSeason";
import { pickSeasonal, type Season } from "@/cms/lib/seasonalMedia";
import { useLocale } from "@/contexts/LocaleContext";
import { publicRows } from "@/lib/publicBackend";


type ContentMap = Record<string, { type: string; value: any; alt?: string; newTab?: boolean } & Record<string, any>>;
type Scope = "page" | "global";
const GLOBAL_SLUG = "_global";

type Ctx = {
  isEditor: boolean;
  editMode: boolean;
  setEditMode: (v: boolean) => void;
  pageSlug: string | null;
  season: Season;
  get: (key: string, fallback?: string, scope?: Scope) => string;
  getEn: (key: string, scope?: Scope) => string | undefined;
  getImage: (key: string, fallback: string, scope?: Scope) => { src: string; alt?: string; href?: string; newTab?: boolean };
  getRawImage: (key: string, scope?: Scope) => Record<string, any> | undefined;
  save: (key: string, type: "text" | "image" | "link", value: string, alt?: string, extra?: Record<string, any>, scope?: Scope) => Promise<void>;
  getLink: (key: string, fallbackLabel: string, fallbackHref: string, scope?: Scope) => { label: string; href: string; newTab?: boolean; label_en?: string };
  reload: () => Promise<void>;
};

const CmsEditContext = createContext<Ctx | null>(null);

function pathToSlug(pathname: string): string | null {
  const match = FRONTEND_PAGES.find((p) => !p.is_dynamic && p.path === pathname);
  if (match) return match.slug;
  // Dynamic suite detail pages: /suites/:slug → "suite-<slug>"
  const m = pathname.match(/^\/suites\/([a-z0-9-]+)\/?$/i);
  if (m) return `suite-${m[1]}`;
  return null;
}

export function CmsEditProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pageSlug = pathToSlug(location.pathname);
  const [isEditor, setIsEditor] = useState(false);
  const [editMode, setEditModeState] = useState(false);
  const [content, setContent] = useState<ContentMap>({});
  const [globalContent, setGlobalContent] = useState<ContentMap>({});
  const season = useSeason();
  const { locale } = useLocale();
  // Inhalte werden nur auf Deutsch verwaltet. Im EN-Modus ist Bearbeiten gesperrt –
  // Texte kommen ausschließlich aus dem Übersetzungstool, Bilder werden vom DE übernommen.
  const effectiveEditMode = locale === "de" ? editMode : false;

  // Editor-Status prüfen
  useEffect(() => {
    const hasAuthSession = Object.keys(localStorage).some(
      (key) => key.startsWith("sb-") && key.endsWith("-auth-token"),
    );
    const wantsEditing = new URLSearchParams(location.search).get("edit") === "1"
      || localStorage.getItem("cms.editMode") === "1";
    if (!hasAuthSession && !wantsEditing) return;

    let mounted = true;
    const check = async () => {
      const { data: { session } } = await (await getSupabase()).auth.getSession();
      if (!session) { if (mounted) setIsEditor(false); return; }
      const { data: roles } = await ((await getSupabase()) as any)
        .from("user_roles").select("role").eq("user_id", session.user.id);
      const ok = !!(roles as Array<{ role: string }> | null)?.some(
        (r) => r.role === "admin" || r.role === "editor"
      );
      if (mounted) setIsEditor(ok);
    };
    check();
    let unsubscribe: (() => void) | undefined;
    getSupabase().then((sb) => {
      const { data: sub } = sb.auth.onAuthStateChange(() => check());
      if (!mounted) sub.subscription.unsubscribe();
      else unsubscribe = () => sub.subscription.unsubscribe();
    });
    return () => { mounted = false; unsubscribe?.(); };
  }, [location.search]);

  // Edit-Mode persistieren – ?edit=1 in URL aktiviert den Modus automatisch
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("edit") === "1") {
      setEditModeState(true);
      localStorage.setItem("cms.editMode", "1");
      return;
    }
    const stored = localStorage.getItem("cms.editMode");
    if (stored === "1") setEditModeState(true);
  }, [location.search]);
  const setEditMode = (v: boolean) => {
    setEditModeState(v);
    localStorage.setItem("cms.editMode", v ? "1" : "0");
  };

  // Content für aktuelle Seite + global laden
  const reload = useCallback(async () => {
    const slugs = [GLOBAL_SLUG, ...(pageSlug ? [pageSlug] : [])];
    const encodedSlugs = slugs.map((slug) => `"${slug}"`).join(",");
    const data = await publicRows<{ page_slug: string; section_key: string; content: Record<string, any>; is_active: boolean }>(
      "cms_sections",
      `select=page_slug,section_key,content,is_active&page_slug=in.(${encodeURIComponent(encodedSlugs)})`,
    ).catch(() => []);
    const pageMap: ContentMap = {};
    const globalMap: ContentMap = {};
    (data ?? []).forEach((r: any) => {
      if (r.is_active === false) return;
      const target = r.page_slug === GLOBAL_SLUG ? globalMap : pageMap;
      target[r.section_key] = r.content ?? {};
    });
    setContent(pageMap);
    setGlobalContent(globalMap);
  }, [pageSlug]);

  useEffect(() => { reload(); }, [reload]);

  const pick = (key: string, scope: Scope) =>
    scope === "global" ? globalContent[key] : content[key];

  const get = useCallback(
    (key: string, fallback = "", scope: Scope = "page") => (pick(key, scope)?.value as string) ?? fallback,
    [content, globalContent]
  );

  const getEn = useCallback(
    (key: string, scope: Scope = "page") => {
      const c = pick(key, scope) as any;
      const v = c?.value_en;
      return typeof v === "string" && v.trim() ? v : undefined;
    },
    [content, globalContent]
  );

  const getImage = useCallback(
    (key: string, fallback: string, scope: Scope = "page") => {
      const c = pick(key, scope) as any;
      const picked = pickSeasonal(c as any, season, { src: fallback, alt: undefined });
      return {
        src: picked.src || fallback,
        alt: picked.alt,
        href: c?.href as string | undefined,
        newTab: c?.newTab as boolean | undefined,
      };
    },
    [content, globalContent, season]
  );

  const getRawImage = useCallback(
    (key: string, scope: Scope = "page") => pick(key, scope) as any,
    [content, globalContent]
  );

  const getLink = useCallback(
    (key: string, fallbackLabel: string, fallbackHref: string, scope: Scope = "page") => {
      const c = pick(key, scope) as any;
      return {
        label: (c?.value as string) || fallbackLabel,
        label_en: typeof c?.value_en === "string" && c.value_en.trim() ? (c.value_en as string) : undefined,
        href: (c?.alt as string) || fallbackHref,
        newTab: c?.newTab as boolean | undefined,
      };
    },
    [content, globalContent]
  );

  const save = useCallback(
    async (key: string, type: "text" | "image" | "link", value: string, alt?: string, extra?: Record<string, any>, scope: Scope = "page") => {
      const slug = scope === "global" ? GLOBAL_SLUG : pageSlug;
      if (!slug) return;
      const { data: { user } } = await (await getSupabase()).auth.getUser();

      // Auto-Übersetzung für Text/Link-Labels (DE → EN), damit die englische Seite
      // ohne manuellen Schritt aktualisiert wird. Bilder & Konfigwerte bleiben unübersetzt.
      let value_en: string | undefined;
      if ((type === "text" || type === "link") && typeof value === "string" && value.trim()) {
        const looksLikeHtml = /<\/?[a-z][\s\S]*>|&nbsp;|&amp;|&#\d+;/i.test(value);
        try {
          const { data: tr, error: trErr } = await (await getSupabase()).functions.invoke("translate-content", {
            body: {
              text: value,
              source_lang: "DE",
              target_lang: "EN-GB",
              html: looksLikeHtml,
              source_table: "cms_sections",
              source_slug: slug,
              source_field: key,
            },
          });
          if (!trErr && tr?.text) value_en = tr.text as string;
        } catch (e) {
          console.warn("Auto-Übersetzung fehlgeschlagen", e);
        }
      }

      const payload: any = {
        page_slug: slug,
        section_key: key,
        content: {
          type,
          value,
          ...(alt !== undefined ? { alt } : {}),
          ...(extra ?? {}),
          ...(value_en !== undefined ? { value_en } : {}),
        },
        updated_by: user?.id ?? null,
        is_active: true,
      };
      const { error } = await ((await getSupabase()) as any)
        .from("cms_sections")
        .upsert(payload, { onConflict: "page_slug,section_key" });
      if (error) throw error;
      const entry = { type, value, alt, ...(extra ?? {}), ...(value_en !== undefined ? { value_en } : {}) };
      if (scope === "global") {
        setGlobalContent((prev) => ({ ...prev, [key]: entry }));
      } else {
        setContent((prev) => ({ ...prev, [key]: entry }));
      }
    },
    [pageSlug]
  );

  return (
    <CmsEditContext.Provider value={{ isEditor, editMode: effectiveEditMode, setEditMode, pageSlug, season, get, getEn, getImage, getRawImage, getLink, save, reload }}>

      {children}
    </CmsEditContext.Provider>
  );
}

export function useCmsEdit() {
  const ctx = useContext(CmsEditContext);
  if (!ctx) throw new Error("useCmsEdit must be used inside CmsEditProvider");
  return ctx;
}
